/**
 * Marketplace Layout Component
 * Shared layout with header, footer, bottom nav and floating cart for all marketplace pages
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search, ShoppingCart, ShoppingBag, User, Heart, Menu, X, Store, ChevronDown,
  MapPin, Truck, LogOut, Package, Home, Shield, BadgeCheck,
  CreditCard, Grid3X3, Facebook, Instagram, Twitter
} from 'lucide-react';
import api from '../../services/api';
import SearchAutocomplete from './SearchAutocomplete';

// Brand colors - Professional & Modern
const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
  lightGray: '#f8fafc',
  white: '#ffffff'
};

// Brand Logo URL
const BRAND_LOGO = '/logo.png';

// Shared Header Component — navy dark style matching Landing
export const MarketplaceHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [cityName, setCityName] = useState(null);
  const userMenuRef = useRef(null);

  const loadCartCount = React.useCallback(async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const response = await api.get(`/marketplace/cart?customer_token=${token}`);
      setCartCount(response.data.items?.length || 0);
    } catch (err) {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const customerData = localStorage.getItem('customer_data');
    setIsLoggedIn(!!token);
    if (customerData) {
      try {
        const data = JSON.parse(customerData);
        setCustomerName(data.full_name || data.email || 'Mi cuenta');
      } catch (err) {}
    }
    loadCartCount();
    // Load cached city
    const cached = localStorage.getItem('userCityName');
    if (cached) setCityName(cached);
  }, [location, loadCartCount]);

  useEffect(() => {
    const onCartUpdated = () => loadCartCount();
    window.addEventListener('cart-updated', onCartUpdated);
    return () => window.removeEventListener('cart-updated', onCartUpdated);
  }, [loadCartCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    setIsLoggedIn(false);
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50" style={{ backgroundColor: COLORS.darkSlate }}>
      <div className="max-w-7xl mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between gap-3">
          {/* Logo + Location */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img src={BRAND_LOGO} alt="Todo en Local" className="h-9 sm:h-11 w-auto brightness-0 invert" />
            </Link>
            {/* City indicator — desktop */}
            <div className="hidden md:flex items-center gap-1.5 text-white/70 cursor-default pl-3 border-l border-white/10">
              <MapPin className="w-3.5 h-3.5" />
              <span className="text-xs leading-tight">
                {cityName ? (
                  <><span className="text-white/50 block text-[10px]">Enviar a</span><span className="font-medium text-white text-xs">{cityName}</span></>
                ) : (
                  <span className="text-white/50">Tu ubicacion</span>
                )}
              </span>
            </div>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-2xl mx-2 sm:mx-5">
            <SearchAutocomplete
              placeholder="Buscar productos, tiendas..."
              className="w-full"
              variant="header"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-1.5">
            <Link to="/favoritos" className="hidden sm:flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors">
              <Heart className="w-5 h-5 text-white" />
              <span className="text-[10px] text-white/60 mt-0.5">Favoritos</span>
            </Link>
            <Link to="/carrito" className="hidden sm:flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors relative">
              <ShoppingBag className="w-5 h-5 text-white" />
              <span className="text-[10px] text-white/60 mt-0.5">Carrito</span>
              {cartCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] rounded-full text-white text-[10px] flex items-center justify-center font-bold" style={{ backgroundColor: COLORS.orange }}>
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </Link>

            {/* Mobile user menu */}
            <div className="relative sm:hidden" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-2 rounded-lg transition-colors hover:bg-white/10"
              >
                <User className="w-5 h-5 text-white" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-white text-[9px] flex items-center justify-center font-bold" style={{ backgroundColor: COLORS.orange }}>
                    {cartCount}
                  </span>
                )}
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                  {cityName && (
                    <>
                      <div className="flex items-center gap-2 px-4 py-2.5 text-slate-500">
                        <MapPin className="w-4 h-4" />
                        <span className="text-xs">{cityName}</span>
                      </div>
                      <div className="border-t border-gray-100 my-1" />
                    </>
                  )}
                  {isLoggedIn ? (
                    <>
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="font-medium text-slate-900 truncate text-sm">{customerName}</p>
                      </div>
                      <Link to="/mi-cuenta" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                        <User className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                        <span className="font-medium text-slate-700">Mi Cuenta</span>
                      </Link>
                      <Link to="/mi-cuenta?tab=orders" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                        <Package className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                        <span className="font-medium text-slate-700">Mis Pedidos</span>
                      </Link>
                    </>
                  ) : (
                    <Link to="/auth" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                      <User className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                      <span className="font-medium text-slate-700">Ingresar</span>
                    </Link>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                  <Link to="/carrito" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                    <ShoppingBag className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                    <span className="font-medium text-slate-700">Carrito</span>
                    {cartCount > 0 && (
                      <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-md text-white" style={{ backgroundColor: COLORS.orange }}>{cartCount}</span>
                    )}
                  </Link>
                  <Link to="/favoritos" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors" onClick={() => setShowUserMenu(false)}>
                    <Heart className="w-5 h-5" style={{ color: COLORS.navyBlue }} />
                    <span className="font-medium text-slate-700">Favoritos</span>
                  </Link>
                  {isLoggedIn && (
                    <>
                      <div className="border-t border-gray-100 my-1" />
                      <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors w-full text-left">
                        <LogOut className="w-5 h-5 text-red-500" />
                        <span className="font-medium text-red-600">Cerrar sesion</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Desktop auth */}
            {isLoggedIn ? (
              <div className="relative hidden sm:block" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <User className="w-5 h-5 text-white" />
                  <span className="text-[10px] text-white/60 mt-0.5 truncate max-w-[60px]">{customerName.split(' ')[0] || 'Cuenta'}</span>
                </button>
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 z-50 py-1">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="font-medium text-slate-900 truncate text-sm">{customerName}</p>
                        <p className="text-xs text-slate-500">Cliente</p>
                      </div>
                      <Link to="/mi-cuenta" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-slate-700" onClick={() => setShowUserMenu(false)}>
                        <User className="w-4 h-4" /> Mi cuenta
                      </Link>
                      <Link to="/mi-cuenta?tab=orders" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-slate-700" onClick={() => setShowUserMenu(false)}>
                        <Package className="w-4 h-4" /> Mis pedidos
                      </Link>
                      <Link to="/favoritos" className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-slate-700" onClick={() => setShowUserMenu(false)}>
                        <Heart className="w-4 h-4" /> Favoritos
                      </Link>
                      <div className="border-t my-1" />
                      <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 text-red-600 w-full text-left">
                        <LogOut className="w-4 h-4" /> Cerrar sesion
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                className="hidden sm:flex flex-col items-center p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <User className="w-5 h-5 text-white" />
                <span className="text-[10px] text-white/60 mt-0.5">Ingresar</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// TikTok icon (Lucide doesn't have one)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.46a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.11z"/>
  </svg>
);

// Shared Footer Component - Enhanced with social, payments, security
export const MarketplaceFooter = () => {
  const [socialLinks, setSocialLinks] = useState({ facebook: null, instagram: null, twitter: null, tiktok: null });

  useEffect(() => {
    api.get('/system-config/social-links')
      .then(res => setSocialLinks(res.data))
      .catch(() => {});
  }, []);

  const socialItems = [
    { key: 'facebook', url: socialLinks.facebook, icon: Facebook },
    { key: 'instagram', url: socialLinks.instagram, icon: Instagram },
    { key: 'twitter', url: socialLinks.twitter, icon: Twitter },
    { key: 'tiktok', url: socialLinks.tiktok, icon: TikTokIcon },
  ].filter(s => s.url);

  return (
  <footer style={{ backgroundColor: '#0a0f1a' }} className="text-slate-400 py-12 pb-28 sm:pb-12">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
        {/* Brand column */}
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            <img
              src={BRAND_LOGO}
              alt="Todo en Local"
              className="h-8 w-auto brightness-0 invert"
            />
          </div>
          <p className="text-sm mb-4">
            La plataforma que conecta comercios locales con sus comunidades.
          </p>
          {/* Social media - dynamic */}
          {socialItems.length > 0 && (
            <div className="flex items-center gap-3">
              {socialItems.map(({ key, url, icon: Icon }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors">
                  <Icon className="w-4 h-4 text-slate-300" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Comprar</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/buscar" className="hover:text-white transition-colors">Buscar Productos</Link></li>
            <li><Link to="/categorias" className="hover:text-white transition-colors">Categorias</Link></li>
            <li><Link to="/buscar?sort=discount" className="hover:text-white transition-colors">Ofertas</Link></li>
            <li><Link to="/tiendas" className="hover:text-white transition-colors">Tiendas</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Para Negocios</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/register" className="hover:text-white transition-colors">Registrar Tienda</Link></li>
            <li><Link to="/como-funciona" className="hover:text-white transition-colors">Como Funciona</Link></li>
            <li><Link to="/precios" className="hover:text-white transition-colors">Precios</Link></li>
            <li><Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Informacion</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/acerca" className="hover:text-white transition-colors">Acerca de Nosotros</Link></li>
            <li><Link to="/contacto" className="hover:text-white transition-colors">Contacto</Link></li>
            <li><Link to="/privacidad" className="hover:text-white transition-colors">Politica de Privacidad</Link></li>
            <li><Link to="/terminos" className="hover:text-white transition-colors">Terminos y Condiciones</Link></li>
            <li><Link to="/faq" className="hover:text-white transition-colors">Preguntas Frecuentes</Link></li>
          </ul>
        </div>

        {/* Security & Payment column */}
        <div>
          <h4 className="text-white font-semibold mb-4">Seguridad</h4>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-green-400" />
              <span>Compra Protegida</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <BadgeCheck className="w-4 h-4 text-blue-400" />
              <span>Tiendas Verificadas</span>
            </div>
            <div className="mt-4">
              <p className="text-xs text-slate-500 mb-2">Aceptamos:</p>
              <div className="flex flex-wrap gap-1.5">
                <span className="px-2 py-1 rounded bg-slate-800 text-[9px] font-bold text-slate-300">VISA</span>
                <span className="px-2 py-1 rounded bg-slate-800 text-[9px] font-bold text-slate-300">MC</span>
                <span className="px-2 py-1 rounded bg-slate-800 text-[9px] font-bold text-slate-300">SPEI</span>
                <span className="px-2 py-1 rounded bg-slate-800 text-[9px] font-bold text-slate-300">Efectivo</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
        <p>&copy; {new Date().getFullYear()} Todo en Local. Todos los derechos reservados.</p>
        <div className="flex items-center gap-4 text-xs">
          <Link to="/privacidad" className="hover:text-white transition-colors">Privacidad</Link>
          <Link to="/terminos" className="hover:text-white transition-colors">Terminos</Link>
          <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
        </div>
      </div>
    </div>
  </footer>
  );
};

// Mobile Bottom Navigation
export const MobileBottomNav = () => {
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const loadCartCount = React.useCallback(async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const response = await api.get(`/marketplace/cart?customer_token=${token}`);
      setCartCount(response.data.items?.length || 0);
    } catch (err) {}
  }, []);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('customer_token'));
    loadCartCount();
  }, [location, loadCartCount]);

  useEffect(() => {
    const onCartUpdated = () => loadCartCount();
    window.addEventListener('cart-updated', onCartUpdated);
    return () => window.removeEventListener('cart-updated', onCartUpdated);
  }, [loadCartCount]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 sm:hidden">
      <div className="flex items-center justify-around py-2">
        <Link to="/" className="flex flex-col items-center gap-0.5 px-3 py-1">
          <Home className={`w-5 h-5 ${isActive('/') ? 'text-orange-500' : 'text-slate-500'}`} style={isActive('/') ? { color: COLORS.orange } : {}} />
          <span className={`text-[10px] font-medium ${isActive('/') ? '' : 'text-slate-500'}`} style={isActive('/') ? { color: COLORS.orange } : {}}>Inicio</span>
        </Link>
        <Link to="/categorias" className="flex flex-col items-center gap-0.5 px-3 py-1">
          <Grid3X3 className={`w-5 h-5 ${isActive('/categorias') ? 'text-orange-500' : 'text-slate-500'}`} style={isActive('/categorias') ? { color: COLORS.orange } : {}} />
          <span className={`text-[10px] font-medium ${isActive('/categorias') ? '' : 'text-slate-500'}`} style={isActive('/categorias') ? { color: COLORS.orange } : {}}>Categorias</span>
        </Link>
        <Link to="/carrito" className="flex flex-col items-center gap-0.5 px-3 py-1 relative">
          <ShoppingCart className={`w-5 h-5 ${isActive('/carrito') ? 'text-orange-500' : 'text-slate-500'}`} style={isActive('/carrito') ? { color: COLORS.orange } : {}} />
          {cartCount > 0 && (
            <span className="absolute -top-0.5 right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
              {cartCount > 9 ? '9+' : cartCount}
            </span>
          )}
          <span className={`text-[10px] font-medium ${isActive('/carrito') ? '' : 'text-slate-500'}`} style={isActive('/carrito') ? { color: COLORS.orange } : {}}>Carrito</span>
        </Link>
        <Link to="/favoritos" className="flex flex-col items-center gap-0.5 px-3 py-1">
          <Heart className={`w-5 h-5 ${isActive('/favoritos') ? 'text-orange-500' : 'text-slate-500'}`} style={isActive('/favoritos') ? { color: COLORS.orange } : {}} />
          <span className={`text-[10px] font-medium ${isActive('/favoritos') ? '' : 'text-slate-500'}`} style={isActive('/favoritos') ? { color: COLORS.orange } : {}}>Favoritos</span>
        </Link>
        <Link to={isLoggedIn ? '/mi-cuenta' : '/auth'} className="flex flex-col items-center gap-0.5 px-3 py-1">
          <User className={`w-5 h-5 ${isActive('/mi-cuenta') || isActive('/auth') ? 'text-orange-500' : 'text-slate-500'}`} style={isActive('/mi-cuenta') || isActive('/auth') ? { color: COLORS.orange } : {}} />
          <span className={`text-[10px] font-medium ${isActive('/mi-cuenta') || isActive('/auth') ? '' : 'text-slate-500'}`} style={isActive('/mi-cuenta') || isActive('/auth') ? { color: COLORS.orange } : {}}>{isLoggedIn ? 'Cuenta' : 'Ingresar'}</span>
        </Link>
      </div>
    </nav>
  );
};

// Floating Cart Button - desktop only (mobile has bottom nav)
export const FloatingCartButton = () => {
  const [cartInfo, setCartInfo] = useState({ itemCount: 0, total: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  const loadCart = React.useCallback(async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const response = await api.get(`/marketplace/cart?customer_token=${token}`);
      setCartInfo({
        itemCount: response.data.items?.length || 0,
        total: response.data.total || 0
      });
    } catch (err) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    loadCart();
  }, [location, loadCart]);

  useEffect(() => {
    const onCartUpdated = () => loadCart();
    window.addEventListener('cart-updated', onCartUpdated);
    return () => window.removeEventListener('cart-updated', onCartUpdated);
  }, [loadCart]);

  // Don't show on cart/checkout pages
  if (['/carrito', '/checkout'].includes(location.pathname)) {
    return null;
  }

  if (cartInfo.itemCount === 0) return null;

  return (
    <button
      onClick={() => navigate('/carrito')}
      className="fixed bottom-6 right-6 hidden sm:flex items-center gap-3 px-5 py-3 rounded-lg shadow-2xl text-white font-semibold z-40 hover:scale-105 active:scale-95 transition-transform"
      style={{ backgroundColor: COLORS.orange }}
      data-testid="floating-cart-btn"
    >
      <ShoppingCart className="w-5 h-5" />
      <span className="bg-white text-orange-500 px-2 py-0.5 rounded-full text-sm font-bold">
        {cartInfo.itemCount}
      </span>
      <span>${cartInfo.total.toFixed(2)}</span>
    </button>
  );
};

// Main Layout Component
const MarketplaceLayout = ({ children, showHeader = true, showFooter = true, showFloatingCart = true }) => {
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {showHeader && <MarketplaceHeader />}
      <main className="flex-1">
        {children}
      </main>
      {showFooter && <MarketplaceFooter />}
      {showFloatingCart && <FloatingCartButton />}
      <MobileBottomNav />
    </div>
  );
};

export default MarketplaceLayout;
